import type { IdentifierModel, IdentifierValueModel, UUID } from "@overmap-ai/common"
import type { ReactNode } from "react"

import {
	NullableStringEqualsFilterModifier,
	NullableStringNotEqualsFilterModifier,
	StringExcludesFilterModifier,
	StringIncludesFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { IdentifierFilterCell } from "./IdentifierFilterCell"

type IdentifierValue = IdentifierValueModel["value"] | null
type IdentifierFilterValue = IdentifierValueModel["value"] | null

export class IdentifierFilterDef<TIdentifier extends UUID<IdentifierModel>> extends BaseFilterDef<
	IdentifierValue,
	IdentifierFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: IdentifierFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly identifier: TIdentifier

	constructor(identifier: TIdentifier) {
		super()
		this.identifier = identifier
		this.id = identifier.uuid
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringEqualsFilterModifier,
			isValueValid: (_value: IdentifierValue): _value is string => true,
			isFilterValueValid: (_filterValue: IdentifierFilterValue): _filterValue is string | null => true,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringNotEqualsFilterModifier,
			isValueValid: (value: IdentifierValue): value is string => value !== null,
			isFilterValueValid: (_filterValue: IdentifierFilterValue): _filterValue is string | null => true,
		}),
		includes: createFilterModifierConfig({
			modifier: StringIncludesFilterModifier,
			isValueValid: (value: IdentifierValue): value is string => value !== null,
			isFilterValueValid: (filterValue: IdentifierFilterValue): filterValue is string => filterValue !== null,
		}),
		excludes: createFilterModifierConfig({
			modifier: StringExcludesFilterModifier,
			isValueValid: (value: IdentifierValue): value is string => value !== null,
			isFilterValueValid: (filterValue: IdentifierFilterValue): filterValue is string => filterValue !== null,
		}),
	}

	render(props: FilterDefRenderProps<IdentifierFilterDef<TIdentifier>>): ReactNode {
		return <IdentifierFilterCell key={props.filter.id} identifier={this.identifier} {...props} />
	}
}
